import { Grid } from '@mui/material';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import HideIcon from '../../../../../assets/svg/HideIcon';
import ShowIcon from '../../../../../assets/svg/ShowIcon';
import { Driver } from '../../../../../domain/driver/Driver';
import Text from '../../../../../react-components/Text';
import { usePlanMapFilters } from '../../hooks/plan-details/usePlanMapFilters';
import { useRetrieveActivePlanDrivers } from '../../hooks/plan-details/useRetrieveActivePlanDrivers';

const MapFilterElement = styled(Grid)`
  height: 42px;
`;

const EyeSelectorContainer = styled(Grid)`
  width: 48px !important;
  height: 32px;
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export const VehiclesPopoverContent = () => {
  const { setMapFilters, mapFilters } = usePlanMapFilters();
  const intl = useIntl();

  const driversFilters = mapFilters.drivers;

  const { data: drivers } = useRetrieveActivePlanDrivers();
  const allDriversAreEnabled = () =>
    [`unassigned`, ...(drivers ?? []).map((driver) => driver.id)].every(
      (driverId) => driversFilters[driverId] === true
    );

  return (
    <>
      <Grid
        style={{
          padding: `10px`,
          backgroundColor: RoutalPalette.neutral00,
          borderBottom: `1px solid ${RoutalPalette.neutral10}`,
        }}
      >
        <MapFilterElement
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          height={`25px`}
        >
          <Grid item xs={10}>
            <Text variant="body-large" isBold intlId="map_options.filters.vehicles" style={{ cursor: `pointer` }} />
          </Grid>

          <Grid
            item
            xs={2}
            marginRight="6px"
            style={{
              height: `25px`,
              width: `48px`,
              display: `flex`,
              alignItems: `center`,
              justifyContent: `flex-end`,
            }}
          >
            <RoutalTooltip
              title={intl.formatMessage({
                id: `map_options.filters.${allDriversAreEnabled() ? `hide_all` : `show_all`}`,
              })}
            >
              <Grid
                onClick={() => {
                  const newDriversFilters = {
                    [`unassigned`]: !allDriversAreEnabled(),
                    ...(drivers ?? []).reduce((amount, d) => {
                      return {
                        ...amount,
                        [d.id]: !allDriversAreEnabled(),
                      };
                    }, {}),
                  };

                  setMapFilters({
                    ...mapFilters,
                    drivers: newDriversFilters,
                  });
                }}
              >
                {allDriversAreEnabled() ? (
                  <HideIcon width="17px" height="12px" style={{ paddingTop: 8 }} />
                ) : (
                  <ShowIcon width="17px" height="17px" style={{}} />
                )}
              </Grid>
            </RoutalTooltip>
          </Grid>
        </MapFilterElement>
      </Grid>

      <Grid container style={{ padding: `10px`, backgroundColor: RoutalPalette.neutral02n }}>
        <Grid
          key="unassigned-stops"
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
            <div
              style={{
                backgroundColor: RoutalPalette.neutral40,
                width: `5px`,
                height: `30px`,
                borderRadius: `50px`,
              }}
            />
            <Text
              style={{
                margin: `8px`,
              }}
              intlId="map_options.filters.unassigned"
            />
          </Grid>

          <Grid item xs={2}>
            {driversFilters[`unassigned`] === true ? (
              <EyeSelectorContainer
                container
                onClick={() => {
                  const newDriversFilters = { ...driversFilters };
                  newDriversFilters[`unassigned`] = false;
                  setMapFilters({
                    ...mapFilters,
                    drivers: newDriversFilters,
                  });
                }}
              >
                <ShowIcon
                  height="17px"
                  width="17px"
                  style={{ color: RoutalPalette.primary40, cursor: `pointer`, paddingBottom: `4px` }}
                />
              </EyeSelectorContainer>
            ) : (
              <EyeSelectorContainer
                container
                onClick={() => {
                  const newDriversFilters = { ...driversFilters };
                  newDriversFilters[`unassigned`] = true;
                  setMapFilters({
                    ...mapFilters,
                    drivers: newDriversFilters,
                  });
                }}
              >
                <HideIcon
                  height="17px"
                  width="17px"
                  style={{ color: RoutalPalette.secondary60, cursor: `pointer`, paddingTop: `3px` }}
                />
              </EyeSelectorContainer>
            )}
          </Grid>
        </Grid>

        {(drivers ?? []).map((driver: Driver) => (
          <MapFilterElement
            key={driver.id}
            container
            id={`driver-filters-${driver.id}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
              <div
                style={{
                  backgroundColor: driver.color ? driver.color.value : `transparent`,
                  width: `5px`,
                  height: `30px`,
                  borderRadius: `50px`,
                }}
              />

              {driver.label && driver.label !== `` ? (
                <Text
                  style={{
                    margin: `8px`,
                    whiteSpace: `nowrap`,
                    textOverflow: `ellipsis`,
                    overflow: `hidden`,
                    maxWidth: `238px`,
                  }}
                >
                  {driver.label}
                </Text>
              ) : (
                <Text
                  variant="body"
                  intlId="no_name"
                  style={{
                    margin: `8px`,
                  }}
                />
              )}
            </Grid>

            <Grid item xs={2}>
              {driversFilters[driver.id] === true ? (
                <EyeSelectorContainer
                  container
                  onClick={() => {
                    const newDriversFilters = { ...driversFilters };
                    newDriversFilters[driver.id] = false;
                    setMapFilters({
                      ...mapFilters,
                      drivers: newDriversFilters,
                    });
                  }}
                >
                  <ShowIcon
                    height="17px"
                    width="17px"
                    style={{ color: RoutalPalette.primary40, cursor: `pointer`, paddingBottom: `4px` }}
                  />
                </EyeSelectorContainer>
              ) : (
                <EyeSelectorContainer
                  container
                  onClick={() => {
                    const newDriversFilters = { ...driversFilters };
                    newDriversFilters[driver.id] = true;
                    setMapFilters({
                      ...mapFilters,
                      drivers: newDriversFilters,
                    });
                  }}
                >
                  <HideIcon
                    height="17px"
                    width="17px"
                    style={{ color: RoutalPalette.secondary60, cursor: `pointer`, paddingTop: `3px` }}
                  />
                </EyeSelectorContainer>
              )}
            </Grid>
          </MapFilterElement>
        ))}
      </Grid>
    </>
  );
};
